import { marked } from 'marked'
import frontMatter from 'front-matter'
import type { DocumentationCategory, DocumentationSection } from '@/types/documentation'

export interface MarkdownContent {
  content: string
  frontmatter: Record<string, any>
  html: string
}

export interface MarkdownFile {
  path: string
  content: MarkdownContent
}

/**
 * Load and parse a markdown file from the reference directory
 * @param path - Path relative to the reference directory (e.g., '01-BACKEND/00-BACKEND-OVERVIEW.md')
 * @returns Parsed markdown content with frontmatter and HTML
 */
export async function loadMarkdownFile(path: string): Promise<MarkdownContent> {
  // For development, try to load from the content directory in public
  // This is a workaround since Vite dev server doesn't serve static files properly
  let markdown = ''
  
  // Try to fetch from the public content directory
  const fullPath = `/content/${path}?_=${Date.now()}`
  
  const response = await fetch(fullPath, {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache'
    }
  })
  
  if (response.ok) {
    markdown = await response.text()
  } else {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
  
  if (!markdown || markdown.trim().length === 0) {
    throw new Error('Markdown file is empty')
  }

  // Note: Removed HTML detection logic as it was incorrectly treating valid markdown with HTML tags as HTML files
  // Markdown files can contain HTML tags, which is normal and should not trigger placeholder content

  // Parse frontmatter and content with error handling
  let frontmatter = {}
  let content = markdown
  
  try {
    const parsed = frontMatter(markdown)
    frontmatter = parsed.attributes || {}
    content = parsed.body
  } catch {
    // Try to clean malformed frontmatter and retry
    try {
      // Remove malformed frontmatter section and try again
      const cleanedMarkdown = markdown.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/m, '')
      const parsed = frontMatter(cleanedMarkdown)
      frontmatter = parsed.attributes || {}
      content = parsed.body
    } catch {
      // If cleaning also fails, treat the entire file as content
      frontmatter = {}
      content = markdown
    }
  }

  // Convert markdown to HTML - marked v16+ returns a string
  const html = await marked.parse(content)

  return {
    content,
    frontmatter: frontmatter || {},
    html,
  }
}



/**
 * Load multiple markdown files
 * @param paths - Array of paths to load
 * @returns Array of loaded markdown files
 */
export async function loadMultipleMarkdownFiles(paths: string[]): Promise<MarkdownFile[]> {
  const files: MarkdownFile[] = []

  for (const path of paths) {
    try {
      const content = await loadMarkdownFile(path)
      files.push({ path, content })
    } catch {
      // Continue with other files instead of failing completely
    }
  }
  
  return files
}

/**
 * Get available markdown files for the documentation system
 * @returns Array of markdown file paths
 */
export function getAvailableMarkdownFiles(): string[] {
  return [
    // Information
    'app_overview.md',
    'app_dependencies.md',
    'app_structure.md',
    
    // Standards
    'standards/overview.md',
    'standards/API_Route_Standards.md',
    'standards/Code_Quality_Standards.md',
    'standards/Database_Standards.md',
    'standards/Deployment_Standards.md',
    'standards/Documentation_Standards.md',
    'standards/Error_Handling_Standards.md',
    'standards/File-Folder_Naming_Standards.md',
    'standards/Github_Commit_Standards.md',
    'standards/Logging-Monitoring_Standards.md',
    'standards/Performance_Standards.md',
    'standards/Security_Standards.md',
    'standards/Testing_Standards.md',
    
    // Documentation - Backend
    'Backend/backend_overview.md',
    'Backend/backend_structure.md',
    'Backend/CONFIG-OVERVIEW.md',
    'Backend/CONTROLLERS-OVERVIEW.md',
    'Backend/DATA-OVERVIEW.md',
    'Backend/DOCS-OVERVIEW.md',
    'Backend/EMAILS-OVERVIEW.md',
    'Backend/KEYS-OVERVIEW.md',
    'Backend/LOCALES-OVERVIEW.md',
    'Backend/LOGS-OVERVIEW.md',
    'Backend/MIDDLEWARE-OVERVIEW.md',
    'Backend/MIGRATIONS-OVERVIEW.md',
    'Backend/MODELS-OVERVIEW.md',
    'Backend/PUBLIC-OVERVIEW.md',
    'Backend/ROUTES-OVERVIEW.md',
    'Backend/SCRIPTS-OVERVIEW.md',
    'Backend/SERVICES-OVERVIEW.md',
    'Backend/SOCKET-OVERVIEW.md',
    'Backend/TESTS-OVERVIEW.md',
    'Backend/UTILITIES-OVERVIEW.md',
    'Backend/UTILS-OVERVIEW.md',
    'Backend/WORKERS-OVERVIEW.md',
    
    // Documentation - COFE Frontend
    'COFE/cofe_overview.md',
    'COFE/cofe_structure.md',
    'COFE/CYPRESS-OVERVIEW.md',
    'COFE/DIST-OVERVIEW.md',
    'COFE/Src/SRC-OVERVIEW.md',
    'COFE/Src/ASSETS-OVERVIEW.md',
    'COFE/Src/COMPONENTS-OVERVIEW.md',
    'COFE/Src/ROUTER-OVERVIEW.md',
    'COFE/Src/SERVICES-OVERVIEW.md',
    'COFE/Src/STORE-OVERVIEW.md',
    'COFE/Src/VIEWS-OVERVIEW.md',
    
    // Documentation - SENTRAFE Frontend
    'SENTRAFE/sentrafe_overview.md',
    'SENTRAFE/sentrafe_structure.md',
    'SENTRAFE/CYPRESS-OVERVIEW.md',
    'SENTRAFE/DOCS-OVERVIEW.md',
    'SENTRAFE/PLUGINS-OVERVIEW.md',
    'SENTRAFE/SCRIPTS-OVERVIEW.md',
    'SENTRAFE/Src/SRC-OVERVIEW.md',
    'SENTRAFE/Src/API-OVERVIEW.md',
    'SENTRAFE/Src/ASSETS-OVERVIEW.md',
    'SENTRAFE/Src/COMPONENTS-OVERVIEW.md',
    'SENTRAFE/Src/GUARDS-OVERVIEW.md',
    'SENTRAFE/Src/I18N-OVERVIEW.md',
    'SENTRAFE/Src/LAYOUTS-OVERVIEW.md',
    'SENTRAFE/Src/MIXINS-OVERVIEW.md',
    'SENTRAFE/Src/ROUTER-OVERVIEW.md',
    'SENTRAFE/Src/STORE-OVERVIEW.md',
    'SENTRAFE/Src/UTILS-OVERVIEW.md',
    'SENTRAFE/Src/VIEWS-OVERVIEW.md',
    
    // Documentation - Sync Engine
    'Sync-Engine/sync-engine_overview.md',
    'Sync-Engine/sync-engine_structure.md',
    'Sync-Engine/App/APP-OVERVIEW.md',
    'Sync-Engine/App/API-OVERVIEW.md',
    'Sync-Engine/App/CORE-OVERVIEW.md',
    'Sync-Engine/App/DATABASE-OVERVIEW.md',
    'Sync-Engine/App/MODELS-OVERVIEW.md',
    'Sync-Engine/App/MONITORING-OVERVIEW.md',
    'Sync-Engine/App/OPTIMIZATIONS-OVERVIEW.md',
    'Sync-Engine/App/PROCESSORS-OVERVIEW.md',
    'Sync-Engine/App/SERVICES-OVERVIEW.md',
    'Sync-Engine/App/SYNC-OVERVIEW.md',
    'Sync-Engine/App/UTILS-OVERVIEW.md',
    'Sync-Engine/App/WORKERS-OVERVIEW.md',
    'Sync-Engine/INIT-SCRIPTS-OVERVIEW.md',
    'Sync-Engine/LOGS-OVERVIEW.md',
    'Sync-Engine/MONGO-INIT-OVERVIEW.md',
    'Sync-Engine/SCRIPTS-OVERVIEW.md',
    'Sync-Engine/TESTS-OVERVIEW.md',
  ]
}

/**
 * Load all documentation from markdown files
 * @returns Array of DocumentationCategory objects
 */
export async function loadDocumentationFromFiles(): Promise<DocumentationCategory[]> {
  const files = getAvailableMarkdownFiles()
  const markdownFiles = await loadMultipleMarkdownFiles(files)
  

  
  const categories: DocumentationCategory[] = []
  
  // Create the main navigation structure based on the new menu layout
  const mainCategories = [
    {
      slug: 'information',
      title: 'Information',
      description: 'App overview, dependencies, and structure documentation',
      order: 1,
      sections: [
        { slug: 'app-overview', title: 'App Overview', filePath: 'app_overview.md' },
        { slug: 'app-dependencies', title: 'App Dependencies', filePath: 'app_dependencies.md' },
        { slug: 'app-structure', title: 'App Structure', filePath: 'app_structure.md' }
      ]
    },
    {
      slug: 'standards',
      title: 'Standards',
      description: 'Development standards, best practices, and guidelines',
      order: 2,
      sections: [
        { slug: 'overview', title: 'Overview', filePath: 'standards/overview.md' },
        { slug: 'api-routing', title: 'API Routing', filePath: 'standards/API_Route_Standards.md' },
        { slug: 'code-quality', title: 'Code Quality', filePath: 'standards/Code_Quality_Standards.md' },
        { slug: 'database', title: 'Database', filePath: 'standards/Database_Standards.md' },
        { slug: 'deployment', title: 'Deployment', filePath: 'standards/Deployment_Standards.md' },
        { slug: 'documentation', title: 'Documentation', filePath: 'standards/Documentation_Standards.md' },
        { slug: 'error-handling', title: 'Error Handling', filePath: 'standards/Error_Handling_Standards.md' },
        { slug: 'file-folder-naming', title: 'File & Folder Naming', filePath: 'standards/File-Folder_Naming_Standards.md' },
        { slug: 'github-committing', title: 'Github Committing', filePath: 'standards/Github_Commit_Standards.md' },
        { slug: 'logging-monitoring', title: 'Logging & Monitoring', filePath: 'standards/Logging-Monitoring_Standards.md' },
        { slug: 'performance', title: 'Performance', filePath: 'standards/Performance_Standards.md' },
        { slug: 'security', title: 'Security', filePath: 'standards/Security_Standards.md' },
        { slug: 'testing', title: 'Testing', filePath: 'standards/Testing_Standards.md' }
      ]
    },
    {
      slug: 'documentation',
      title: 'Documentation',
      description: 'Component documentation and implementation guides',
      order: 3,
      sections: [
        // Backend Documentation
        { slug: 'backend-overview', title: 'Overview', filePath: 'Backend/backend_overview.md' },
        { slug: 'backend-structure', title: 'Structure', filePath: 'Backend/backend_structure.md' },
        { slug: 'backend-config', title: 'Config', filePath: 'Backend/CONFIG-OVERVIEW.md' },
        { slug: 'backend-controllers', title: 'Controllers', filePath: 'Backend/CONTROLLERS-OVERVIEW.md' },
        { slug: 'backend-data', title: 'Data', filePath: 'Backend/DATA-OVERVIEW.md' },
        { slug: 'backend-docs', title: 'Docs', filePath: 'Backend/DOCS-OVERVIEW.md' },
        { slug: 'backend-emails', title: 'Emails', filePath: 'Backend/EMAILS-OVERVIEW.md' },
        { slug: 'backend-keys', title: 'Keys', filePath: 'Backend/KEYS-OVERVIEW.md' },
        { slug: 'backend-locales', title: 'Locales', filePath: 'Backend/LOCALES-OVERVIEW.md' },
        { slug: 'backend-logs', title: 'Logs', filePath: 'Backend/LOGS-OVERVIEW.md' },
        { slug: 'backend-middleware', title: 'Middelware', filePath: 'Backend/MIDDLEWARE-OVERVIEW.md' },
        { slug: 'backend-migrations', title: 'Migrations', filePath: 'Backend/MIGRATIONS-OVERVIEW.md' },
        { slug: 'backend-models', title: 'Models', filePath: 'Backend/MODELS-OVERVIEW.md' },
        { slug: 'backend-public', title: 'Public', filePath: 'Backend/PUBLIC-OVERVIEW.md' },
        { slug: 'backend-routes', title: 'Routes', filePath: 'Backend/ROUTES-OVERVIEW.md' },
        { slug: 'backend-scripts', title: 'Scripts', filePath: 'Backend/SCRIPTS-OVERVIEW.md' },
        { slug: 'backend-services', title: 'Services', filePath: 'Backend/SERVICES-OVERVIEW.md' },
        { slug: 'backend-socket', title: 'Socket', filePath: 'Backend/SOCKET-OVERVIEW.md' },
        { slug: 'backend-tests', title: 'Tests', filePath: 'Backend/TESTS-OVERVIEW.md' },
        { slug: 'backend-utilities', title: 'Utilities', filePath: 'Backend/UTILITIES-OVERVIEW.md' },
        { slug: 'backend-utils', title: 'Utils', filePath: 'Backend/UTILS-OVERVIEW.md' },
        { slug: 'backend-workers', title: 'Workers', filePath: 'Backend/WORKERS-OVERVIEW.md' },
        
        // COFE Frontend Documentation
        { slug: 'cofe-overview', title: 'Overview', filePath: 'COFE/cofe_overview.md' },
        { slug: 'cofe-structure', title: 'Structure', filePath: 'COFE/cofe_structure.md' },
        { slug: 'cofe-cypress', title: 'Cypress', filePath: 'COFE/CYPRESS-OVERVIEW.md' },
        { slug: 'cofe-dist', title: 'Dist', filePath: 'COFE/DIST-OVERVIEW.md' },
        { slug: 'cofe-src-overview', title: 'Src Overview', filePath: 'COFE/Src/SRC-OVERVIEW.md' },
        { slug: 'cofe-src-assets', title: 'Assets', filePath: 'COFE/Src/ASSETS-OVERVIEW.md' },
        { slug: 'cofe-src-components', title: 'Components', filePath: 'COFE/Src/COMPONENTS-OVERVIEW.md' },
        { slug: 'cofe-src-router', title: 'Router', filePath: 'COFE/Src/ROUTER-OVERVIEW.md' },
        { slug: 'cofe-src-services', title: 'Services', filePath: 'COFE/Src/SERVICES-OVERVIEW.md' },
        { slug: 'cofe-src-store', title: 'Store', filePath: 'COFE/Src/STORE-OVERVIEW.md' },
        { slug: 'cofe-src-views', title: 'Views', filePath: 'COFE/Src/VIEWS-OVERVIEW.md' },
        
        // SENTRAFE Frontend Documentation
        { slug: 'sentrafe-overview', title: 'Overview', filePath: 'SENTRAFE/sentrafe_overview.md' },
        { slug: 'sentrafe-structure', title: 'Structure', filePath: 'SENTRAFE/sentrafe_structure.md' },
        { slug: 'sentrafe-cypress', title: 'Cypress', filePath: 'SENTRAFE/CYPRESS-OVERVIEW.md' },
        { slug: 'sentrafe-docs', title: 'Docs', filePath: 'SENTRAFE/DOCS-OVERVIEW.md' },
        { slug: 'sentrafe-plugins', title: 'Plugins', filePath: 'SENTRAFE/PLUGINS-OVERVIEW.md' },
        { slug: 'sentrafe-scripts', title: 'Scripts', filePath: 'SENTRAFE/SCRIPTS-OVERVIEW.md' },
        { slug: 'sentrafe-src-overview', title: 'Src Overview', filePath: 'SENTRAFE/Src/SRC-OVERVIEW.md' },
        { slug: 'sentrafe-src-api', title: 'Api', filePath: 'SENTRAFE/Src/API-OVERVIEW.md' },
        { slug: 'sentrafe-src-assets', title: 'Assets', filePath: 'SENTRAFE/Src/ASSETS-OVERVIEW.md' },
        { slug: 'sentrafe-src-components', title: 'Components', filePath: 'SENTRAFE/Src/COMPONENTS-OVERVIEW.md' },
        { slug: 'sentrafe-src-guards', title: 'Guards', filePath: 'SENTRAFE/Src/GUARDS-OVERVIEW.md' },
        { slug: 'sentrafe-src-i18n', title: 'I18n', filePath: 'SENTRAFE/Src/I18N-OVERVIEW.md' },
        { slug: 'sentrafe-src-layouts', title: 'Layouts', filePath: 'SENTRAFE/Src/LAYOUTS-OVERVIEW.md' },
        { slug: 'sentrafe-src-mixins', title: 'Mixins', filePath: 'SENTRAFE/Src/MIXINS-OVERVIEW.md' },
        { slug: 'sentrafe-src-router', title: 'Router', filePath: 'SENTRAFE/Src/ROUTER-OVERVIEW.md' },
        { slug: 'sentrafe-src-store', title: 'Store', filePath: 'SENTRAFE/Src/STORE-OVERVIEW.md' },
        { slug: 'sentrafe-src-utils', title: 'Utils', filePath: 'SENTRAFE/Src/UTILS-OVERVIEW.md' },
        { slug: 'sentrafe-src-views', title: 'Views', filePath: 'SENTRAFE/Src/VIEWS-OVERVIEW.md' },
        
        // Sync Engine Documentation
        { slug: 'sync-engine-overview', title: 'Overview', filePath: 'Sync-Engine/sync-engine_overview.md' },
        { slug: 'sync-engine-structure', title: 'Structure', filePath: 'Sync-Engine/sync-engine_structure.md' },
        { slug: 'sync-engine-app-overview', title: 'App Overview', filePath: 'Sync-Engine/App/APP-OVERVIEW.md' },
        { slug: 'sync-engine-app-api', title: 'Api', filePath: 'Sync-Engine/App/API-OVERVIEW.md' },
        { slug: 'sync-engine-app-core', title: 'Core', filePath: 'Sync-Engine/App/CORE-OVERVIEW.md' },
        { slug: 'sync-engine-app-database', title: 'Database', filePath: 'Sync-Engine/App/DATABASE-OVERVIEW.md' },
        { slug: 'sync-engine-app-models', title: 'Models', filePath: 'Sync-Engine/App/MODELS-OVERVIEW.md' },
        { slug: 'sync-engine-app-monitoring', title: 'Monitoring', filePath: 'Sync-Engine/App/MONITORING-OVERVIEW.md' },
        { slug: 'sync-engine-app-optimizations', title: 'Optimizations', filePath: 'Sync-Engine/App/OPTIMIZATIONS-OVERVIEW.md' },
        { slug: 'sync-engine-app-processors', title: 'Processors', filePath: 'Sync-Engine/App/PROCESSORS-OVERVIEW.md' },
        { slug: 'sync-engine-app-services', title: 'Services', filePath: 'Sync-Engine/App/SERVICES-OVERVIEW.md' },
        { slug: 'sync-engine-app-sync', title: 'Sync', filePath: 'Sync-Engine/App/SYNC-OVERVIEW.md' },
        { slug: 'sync-engine-app-utils', title: 'Utils', filePath: 'Sync-Engine/App/UTILS-OVERVIEW.md' },
        { slug: 'sync-engine-app-workers', title: 'Workers', filePath: 'Sync-Engine/App/WORKERS-OVERVIEW.md' },
        { slug: 'sync-engine-init-scripts', title: 'Init-Scripts', filePath: 'Sync-Engine/INIT-SCRIPTS-OVERVIEW.md' },
        { slug: 'sync-engine-logs', title: 'Logs', filePath: 'Sync-Engine/LOGS-OVERVIEW.md' },
        { slug: 'sync-engine-mongo-init', title: 'Mongo-Init', filePath: 'Sync-Engine/MONGO-INIT-OVERVIEW.md' },
        { slug: 'sync-engine-scripts', title: 'Scripts', filePath: 'Sync-Engine/SCRIPTS-OVERVIEW.md' },
        { slug: 'sync-engine-tests', title: 'Tests', filePath: 'Sync-Engine/TESTS-OVERVIEW.md' }
      ]
    }
  ]
  
  // Create categories with their main sections
  for (const categoryConfig of mainCategories) {
    const category: DocumentationCategory = {
      id: categoryConfig.slug,
      slug: categoryConfig.slug,
      title: categoryConfig.title,
      description: categoryConfig.description,
      order: categoryConfig.order,
      sections: [],
      isActive: true
    }
    
    // Add sections to the category
    for (const sectionConfig of categoryConfig.sections) {
      // Find the corresponding markdown file content
      let markdownContent = ''
      let htmlContent = ''
      
      // Search for the markdown file that matches this section using filePath
      for (const file of markdownFiles) {
        // Try exact match first
        if (file.path === sectionConfig.filePath) {
          markdownContent = file.content.content
          htmlContent = file.content.html
          break
        }
        // Try matching the filename without path
        const fileName = file.path.split('/').pop()
        const sectionFileName = sectionConfig.filePath.split('/').pop()
        if (fileName === sectionFileName) {
          markdownContent = file.content.content
          htmlContent = file.content.html
          break
        }
      }
      

      
      // If no content found, create a placeholder
      if (!markdownContent) {
        markdownContent = `# ${sectionConfig.title}\n\nContent for ${sectionConfig.title} will be loaded here.\n\n**Note**: This file is being loaded from the placeholder. The actual file may not exist or may not be accessible.`
        htmlContent = `<h1>${sectionConfig.title}</h1><p>Content for ${sectionConfig.title} will be loaded here.</p><p><strong>Note</strong>: This file is being loaded from the placeholder. The actual file may not exist or may not be accessible.</p>`
      }
      
      const section: DocumentationSection = {
        id: sectionConfig.slug,
        slug: sectionConfig.slug,
        title: sectionConfig.title,
        summary: `Overview of ${sectionConfig.title.toLowerCase()}`,
        tags: [categoryConfig.slug, sectionConfig.slug],
        lastUpdated: new Date().toISOString(),
        estimatedReadTime: estimateReadTimeMinutes(markdownContent),
        difficulty: 'beginner',
        content: markdownContent,
        html: htmlContent
      }
      
      category.sections.push(section)
    }
    
    categories.push(category)
  }
  
  // Sort categories and sections
  categories.sort((a, b) => a.order - b.order)
  categories.forEach(category => {
    category.sections.sort((a, b) => a.slug.localeCompare(b.slug))
  })
  
  return categories
}

/**
 * Estimate read time in minutes based on word count (~200 wpm)
 */
function estimateReadTimeMinutes(markdown: string): number {
  const text = markdown
    .replace(/`{3}[\s\S]*?`{3}/g, ' ') // remove code blocks
    .replace(/`[^`]*`/g, ' ') // inline code
    .replace(/<[^>]+>/g, ' ') // html tags
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1') // links
    .replace(/[#>*_-]/g, ' ') // common md chars
  const words = text.split(/\s+/).filter(Boolean).length
  const minutes = Math.max(1, Math.round(words / 200))
  return minutes
}
